import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useDialogA11y } from '@/hooks/useDialogA11y';

/**
 * Modale légère partagée du module Comptabilité
 * (pas de dépendance Radix Dialog dans le projet — overlay maison).
 */
export interface AccountingModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode | undefined;
  className?: string | undefined;
  /** Field focused on open; defaults to the first focusable (the close button). */
  initialFocus?: React.RefObject<HTMLElement | null> | undefined;
}

export function AccountingModal({
  open,
  title,
  onClose,
  children,
  footer,
  className,
  initialFocus,
}: AccountingModalProps) {
  const dialogRef = useDialogA11y<HTMLDivElement>({ open, onClose, initialFocus });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl',
          'max-h-[90vh] flex flex-col',
          className
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-slate-500 hover:text-slate-700"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountingModal;
